import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { cloneElement, Fragment, isValidElement, useEffect, useRef } from 'react';
import r from 'render-dom';
import styled from 'styled-components';

import { useOnClickOutside, useToggle } from 'hooks';

import Option from './Option';

const ArrowDown = styled.i.attrs({
    className: 'icon-dropdown',
})`
    color: #999;
    font-size: 20px;
    height: 17px;
    padding-right: 0;

    &::before {
        height: 17px;
    }
`;

const Error = styled.span`
    color: #d20707;
    font-size: 12px;
    font-weight: 500;
    left: 3px;
    position: relative;
    top: -10px;
`;

const Label = styled.span`
    font-size: 14px;
    font-weight: 500;
`;

const OptionContainer = styled(motion.div)`
    background-color: #fff;
    border-radius: 5px;
    box-shadow: rgb(222,222,222) 0px 0px 10px 0px;
    box-sizing: border-box;
    color: #999;
    left: 0px;
    max-height: ${({ isSmallContainer }) => isSmallContainer ? '150px' : '200px'};
    overflow: auto;
    padding: ${({ center }) => center ? '0' : '7px 0 7px 0'};
    position: absolute;
    text-align: ${({ center }) => center ? 'center' : 'left'};
    top: 3px;
    width: 100%;
    z-index: 10;
`;

const Placeholder = styled.span`
    color: #999;
    font-size: 16px;
    margin-left: 7px;
    margin-right: 15px;
`;

const SelectContainer = styled.div`
    align-items: center;
    border-radius: 10px;
    box-sizing: border-box;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    display: flex;
    justify-content: space-between;
    margin: 7px 0 15px;
    min-width: 50px;
    padding: 7px;
    position: relative;
`;

const StyledOption = styled.span`
    align-items: center;
    border-radius: 5px;
    box-sizing: border-box;
    color: #999;
    display: flex;
    font-size: 16px;
    margin: 0 7px;
    overflow: hidden;
    padding: 7px;
    white-space: nowrap;

    &:hover {
        background: #eaeaea;
        color: #777;
    }
`;

const Value = styled.span`
    margin-left: 7px;
    margin-right: 15px;
    overflow: hidden;
    white-space: nowrap;
`;

export default function Select({
    center = false,
    className = '',
    disabled = false,
    isSmallContainer = false,
    label,
    meta = {},
    onChange,
    options,
    placeholder = '',
    style = {},
    value,
}) {
    const [isOpen, toggleIsOpen] = useToggle(false);
    const selectRef = useRef(null);
    const variants = {
        hide: {
            transition: {
                duration: options.length > 8 ? 0.24 : 0.03 * options.length,
                type: 'tween',
            },
        },
        show: {
            transition: {
                duration: options.length > 8 ? 0.32 : 0.03 * options.length,
                type: 'tween',
            },
        },
    };
    const optionType = (r(Option)).type;
    const usesOptionComponent = options.length && options.every((option) => {
        return isValidElement(option) && option.type === optionType;
    });
    const selected = usesOptionComponent
        ? cloneElement(
            options && options.length && options.find((option) => option.props.value === value),
            { onClick: Function.prototype, selected: true }
        )
        : r(Value, [value]);

    useOnClickOutside(selectRef, toggleIsOpen, isOpen);

    useEffect(() => {
        if (isOpen) {
            toggleIsOpen();
        }
    }, [value]);

    return (
        r(Fragment, [
            r(Label, { isRendered: label }, [label]),
            r(SelectContainer, {
                className,
                disabled,
                onClick: !disabled ? () => toggleIsOpen() : Function.prototype,
                options,
                ref: selectRef,
                style,
            }, [
                r(Placeholder, { isRendered: !value }, [placeholder]),
                selected,
                r(ArrowDown),
                r(AnimatePresence, [
                    r(OptionContainer, {
                        animate: isOpen ? variants.show : variants.hide,
                        center,
                        className: 'scroll',
                        exit: variants.hide,
                        initial: variants.hide,
                        isRendered: isOpen,
                        isSmallContainer,
                    }, [
                        usesOptionComponent && options.map((option) => (
                            cloneElement(option, { onClick: onChange })
                        )),
                        !usesOptionComponent && options.map((option) => (
                            r(StyledOption, {
                                isSelected: option === value,
                                key: option,
                                onClick: () => onChange(option),
                            }, [option])
                        )),
                    ]),
                ]),
            ]),
            r(Error, { isRendered: meta.error }, [meta.error]),
        ])
    );
}

Select.propTypes = {
    onChange: PropTypes.func,
    options: PropTypes.array.isRequired,
    selectedValue: PropTypes.string,
};
